/**
 * Format Overrides Panel
 * Edit responsive overrides for layers across different format categories
 */

import React, { useState, useMemo } from 'react';
import {
  Eye,
  EyeOff,
  Smartphone,
  Monitor,
  Square,
  RectangleHorizontal,
  RectangleVertical,
  Maximize2,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  LucideIcon,
} from 'lucide-react';
import { useEditorStore, useTemplateStore } from '@/stores';
import { getFormatCategory } from '@/lib/position/resolver';
import type { FormatCategory, Layer, LayerOverride } from '@/types/layers';

// Category metadata
const CATEGORY_INFO: Record<FormatCategory, {
  label: string;
  description: string;
  icon: LucideIcon;
  examples: string;
}> = {
  tiny: {
    label: 'Tiny',
    description: 'Very small banners',
    icon: Smartphone,
    examples: '120x60, 88x31',
  },
  small: {
    label: 'Small',
    description: 'Compact display ads',
    icon: Square,
    examples: '300x250, 300x100',
  },
  medium: {
    label: 'Medium',
    description: 'Standard sizes',
    icon: Monitor,
    examples: '1080x1080, 1200x628',
  },
  wide: {
    label: 'Wide',
    description: 'Landscape banners',
    icon: RectangleHorizontal,
    examples: '970x90, 728x90',
  },
  tall: {
    label: 'Tall',
    description: 'Portrait/story formats',
    icon: RectangleVertical,
    examples: '1080x1920, 160x600',
  },
  square: {
    label: 'Square',
    description: 'Equal aspect ratio',
    icon: Maximize2,
    examples: '1080x1080, 250x250',
  },
};

const ALL_CATEGORIES: FormatCategory[] = ['tiny', 'small', 'medium', 'wide', 'tall', 'square'];

export function FormatOverridesPanel() {
  const { selectedFormat } = useEditorStore();
  const { currentTemplate, updateLayer, selectedLayerIds } = useTemplateStore();

  const [expandedCategories, setExpandedCategories] = useState<Set<FormatCategory>>(
    new Set(['tiny', 'small', 'wide'])
  );

  // Get current format category
  const currentCategory = useMemo(() => {
    return getFormatCategory(selectedFormat);
  }, [selectedFormat]);

  // Get selected layer
  const selectedLayer = useMemo(() => {
    if (selectedLayerIds.length !== 1 || !currentTemplate) return null;
    return currentTemplate.layers.find(l => l.id === selectedLayerIds[0]) || null;
  }, [selectedLayerIds, currentTemplate]);

  const toggleCategory = (category: FormatCategory) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleOverrideChange = (
    category: FormatCategory,
    field: keyof LayerOverride,
    value: unknown
  ) => {
    if (!selectedLayer) return;

    const currentOverrides = selectedLayer.responsiveOverrides || {};
    const categoryOverride = currentOverrides[category] || {};

    // If value is same as layer default, remove the override
    const layerValue = (selectedLayer as unknown as Record<string, unknown>)[field];
    if (value === layerValue || (value === undefined && layerValue === undefined)) {
      // Remove the override
      const { [field]: _, ...rest } = categoryOverride as Record<string, unknown>;
      const newCategoryOverride = Object.keys(rest).length > 0 ? rest : undefined;

      updateLayer(selectedLayer.id, {
        responsiveOverrides: {
          ...currentOverrides,
          [category]: newCategoryOverride,
        },
      });
    } else {
      // Set the override
      updateLayer(selectedLayer.id, {
        responsiveOverrides: {
          ...currentOverrides,
          [category]: {
            ...categoryOverride,
            [field]: value,
          },
        },
      });
    }
  };

  const clearCategoryOverrides = (category: FormatCategory) => {
    if (!selectedLayer) return;

    const currentOverrides = selectedLayer.responsiveOverrides || {};
    const { [category]: _, ...rest } = currentOverrides;

    updateLayer(selectedLayer.id, {
      responsiveOverrides: Object.keys(rest).length > 0 ? rest : undefined,
    });
  };

  const getOverrideValue = (category: FormatCategory, field: keyof LayerOverride) => {
    if (!selectedLayer?.responsiveOverrides) return undefined;
    const override = selectedLayer.responsiveOverrides[category];
    if (!override) return undefined;
    return (override as Record<string, unknown>)[field];
  };

  const hasOverride = (category: FormatCategory, field: keyof LayerOverride) => {
    return getOverrideValue(category, field) !== undefined;
  };

  const getCategoryOverrideCount = (category: FormatCategory) => {
    if (!selectedLayer?.responsiveOverrides) return 0;
    const override = selectedLayer.responsiveOverrides[category];
    if (!override) return 0;
    return Object.keys(override).length;
  };

  if (!selectedLayer) {
    return (
      <div className="text-center py-8 text-fg-subtle">
        <p className="text-sm">Select a layer to edit its responsive overrides</p>
        <p className="text-xs mt-2">
          Current format: <span className="text-fg">{currentCategory}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current format indicator */}
      <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
        <div className="flex items-center gap-2">
          {React.createElement(CATEGORY_INFO[currentCategory].icon, {
            size: 16,
            className: 'text-primary',
          })}
          <span className="text-sm font-medium text-fg">
            Current: {CATEGORY_INFO[currentCategory].label}
          </span>
        </div>
        <p className="text-xs text-fg-muted mt-1">
          {selectedFormat.name} ({selectedFormat.width}x{selectedFormat.height})
        </p>
      </div>

      {/* Layer info */}
      <div>
        <h4 className="label">Layer: {selectedLayer.name}</h4>
        <p className="text-xs text-fg-subtle">
          Define how this layer appears in different format categories
        </p>
      </div>

      {/* Category overrides */}
      <div className="space-y-2">
        {ALL_CATEGORIES.map((category) => {
          const info = CATEGORY_INFO[category];
          const isExpanded = expandedCategories.has(category);
          const overrideCount = getCategoryOverrideCount(category);
          const isCurrent = category === currentCategory;

          // Get effective visibility for this category
          const visibilityOverride = getOverrideValue(category, 'visible');
          const effectiveVisible = visibilityOverride !== undefined
            ? visibilityOverride
            : selectedLayer.visible;

          return (
            <div
              key={category}
              className={`
                border rounded-lg overflow-hidden
                ${isCurrent ? 'border-primary/50 bg-primary/5' : 'border-white/10'}
              `}
            >
              {/* Category header */}
              <div className="flex items-center">
                <button
                  onClick={() => toggleCategory(category)}
                  className="flex-1 flex items-center gap-2 px-3 py-2 hover:bg-white/5"
                >
                  {isExpanded ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                  <info.icon size={14} className={isCurrent ? 'text-primary' : ''} />
                  <span className="text-sm font-medium">{info.label}</span>
                  {isCurrent && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-primary/20 text-primary rounded">
                      Current
                    </span>
                  )}
                  {overrideCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/10 rounded ml-auto">
                      {overrideCount} override{overrideCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </button>

                {/* Quick visibility toggle */}
                <button
                  onClick={() => handleOverrideChange(
                    category,
                    'visible',
                    hasOverride(category, 'visible') ? undefined : !effectiveVisible
                  )}
                  className={`
                    p-2 transition-colors
                    ${effectiveVisible
                      ? 'text-fg hover:text-fg-muted'
                      : 'text-fg-subtle hover:text-fg'
                    }
                    ${hasOverride(category, 'visible') ? 'bg-primary/10' : ''}
                  `}
                  title={effectiveVisible ? 'Visible' : 'Hidden'}
                >
                  {effectiveVisible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>

              {/* Category details */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-white/5">
                  <p className="text-xs text-fg-subtle mb-3">
                    {info.description} • {info.examples}
                  </p>

                  {/* Override controls */}
                  <div className="space-y-3">
                    {/* Visibility */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-fg-muted">Visibility</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOverrideChange(category, 'visible', true)}
                          className={`
                            px-2 py-1 text-xs rounded transition-colors
                            ${effectiveVisible && hasOverride(category, 'visible')
                              ? 'bg-primary text-white'
                              : effectiveVisible && !hasOverride(category, 'visible')
                                ? 'bg-white/10 text-fg'
                                : 'bg-white/5 text-fg-muted hover:bg-white/10'
                            }
                          `}
                        >
                          Show
                        </button>
                        <button
                          onClick={() => handleOverrideChange(category, 'visible', false)}
                          className={`
                            px-2 py-1 text-xs rounded transition-colors
                            ${!effectiveVisible && hasOverride(category, 'visible')
                              ? 'bg-red-500/80 text-white'
                              : !effectiveVisible && !hasOverride(category, 'visible')
                                ? 'bg-white/10 text-fg'
                                : 'bg-white/5 text-fg-muted hover:bg-white/10'
                            }
                          `}
                        >
                          Hide
                        </button>
                        {hasOverride(category, 'visible') && (
                          <button
                            onClick={() => handleOverrideChange(category, 'visible', undefined)}
                            className="p-1 text-fg-subtle hover:text-fg"
                            title="Reset to default"
                          >
                            <RotateCcw size={12} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Opacity */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-fg-muted">Opacity</span>
                        {hasOverride(category, 'opacity') && (
                          <button
                            onClick={() => handleOverrideChange(category, 'opacity', undefined)}
                            className="p-1 text-fg-subtle hover:text-fg"
                            title="Reset to default"
                          >
                            <RotateCcw size={10} />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.05}
                          value={
                            (getOverrideValue(category, 'opacity') as number | undefined) ??
                            selectedLayer.opacity
                          }
                          onChange={(e) =>
                            handleOverrideChange(category, 'opacity', parseFloat(e.target.value))
                          }
                          className="flex-1"
                        />
                        <span className="text-xs w-8 text-right">
                          {Math.round(
                            ((getOverrideValue(category, 'opacity') as number | undefined) ??
                              selectedLayer.opacity) * 100
                          )}%
                        </span>
                      </div>
                    </div>

                    {/* Clear all overrides for this category */}
                    {overrideCount > 0 && (
                      <button
                        onClick={() => clearCategoryOverrides(category)}
                        className="w-full py-1.5 text-xs text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded transition-colors"
                      >
                        Clear all {info.label} overrides
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <div className="p-3 bg-white/5 rounded-lg">
        <h4 className="text-xs font-medium text-fg mb-1">How it works</h4>
        <p className="text-[11px] text-fg-subtle leading-relaxed">
          Overrides let you customize how layers appear in different ad sizes.
          For example, hide detailed elements on tiny ads or adjust opacity
          for better readability on wide banners.
        </p>
      </div>
    </div>
  );
}
